import { FormEvent, useState } from 'react';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { api, extractError } from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { Button } from '../../components/ui/Button';
import { PhoneInput } from '../../components/ui/PhoneInput';
import { AccountPageHeader } from '../../components/account/AccountPageHeader';
import { Field } from '../../components/ui/Field';
import { useFormErrors } from '../../hooks/useFormErrors';
import {
  combine,
  nif as nifRule,
  password as passwordRule,
  phonePT,
  required,
} from '../../lib/validate';
import styles from './ProfilePage.module.scss';

type StrengthScore = 0 | 1 | 2 | 3 | 4;

function passwordStrength(pw: string): { score: StrengthScore; label: string } {
  if (!pw) return { score: 0, label: 'sem palavra-passe' };
  let raw = 0;
  if (pw.length >= 8) raw++;
  if (pw.length >= 12) raw++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) raw++;
  if (/\d/.test(pw)) raw++;
  if (/[^a-zA-Z0-9]/.test(pw)) raw++;
  const score = Math.max(1, Math.min(raw, 4)) as StrengthScore;
  const labels: Record<StrengthScore, string> = {
    0: 'sem palavra-passe',
    1: 'demasiado fraca',
    2: 'fraca',
    3: 'média',
    4: 'forte',
  };
  return { score, label: labels[score] };
}

type ProfileFieldKey = 'firstName' | 'lastName' | 'phone' | 'nif';
type PasswordFieldKey = 'currentPassword' | 'newPassword' | 'confirmPassword';

type Feedback = { kind: 'success' | 'error'; text: string } | null;

export default function ProfilePage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const fetchMe = useAuthStore((s) => s.fetchMe);

  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [nif, setNif] = useState(user?.nif ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileFeedback, setProfileFeedback] = useState<Feedback>(null);
  const [passwordFeedback, setPasswordFeedback] = useState<Feedback>(null);

  const profileErrors = useFormErrors<ProfileFieldKey>();
  const passwordErrors = useFormErrors<PasswordFieldKey>();

  const strength = passwordStrength(newPassword);

  const validateProfile = (): Partial<Record<ProfileFieldKey, string>> => {
    const next: Partial<Record<ProfileFieldKey, string>> = {};
    const firstNameErr = required(firstName);
    if (firstNameErr) next.firstName = firstNameErr;
    const lastNameErr = required(lastName);
    if (lastNameErr) next.lastName = lastNameErr;
    const phoneErr = phonePT(phone);
    if (phoneErr) next.phone = phoneErr;
    const nifErr = nifRule(nif);
    if (nifErr) next.nif = nifErr;
    return next;
  };

  const validatePassword = (): Partial<Record<PasswordFieldKey, string>> => {
    const next: Partial<Record<PasswordFieldKey, string>> = {};
    const currentErr = required(currentPassword);
    if (currentErr) next.currentPassword = currentErr;
    const newErr = combine(required, passwordRule)(newPassword);
    if (newErr) next.newPassword = newErr;
    const confirmErr = combine(required, passwordRule)(confirmPassword);
    if (confirmErr) next.confirmPassword = confirmErr;
    else if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      next.confirmPassword = t.validation.passwordsDoNotMatch;
    }
    return next;
  };

  const saveProfile = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setProfileFeedback(null);
    const errs = validateProfile();
    profileErrors.setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      await api.patch('/account/profile', { firstName, lastName, phone, nif });
      await fetchMe();
      setProfileFeedback({ kind: 'success', text: 'Perfil atualizado.' });
    } catch (err) {
      setProfileFeedback({
        kind: 'error',
        text: extractError(err, 'Falha ao atualizar perfil'),
      });
    }
  };

  const changePassword = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setPasswordFeedback(null);
    const errs = validatePassword();
    passwordErrors.setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      await api.post('/account/change-password', { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordFeedback({ kind: 'success', text: 'Palavra-passe alterada.' });
    } catch (err) {
      setPasswordFeedback({
        kind: 'error',
        text: extractError(err, 'Falha ao alterar palavra-passe'),
      });
    }
  };

  return (
    <>
      <SEO title={t.account.profile} noindex />
      <AccountPageHeader
        title={t.account.profile}
        lead="Atualiza os teus dados pessoais e palavra-passe."
      />

      <section className={styles.section}>
        <form onSubmit={saveProfile} className={styles.form} noValidate>
          <Field
            label={t.account.email}
            hint={t.account.emailHint}
            className={styles.field}
          >
            <input value={user?.email ?? ''} readOnly disabled autoComplete="email" />
          </Field>
          <div className={styles.row}>
            <Field
              label="Nome"
              required
              error={profileErrors.errors.firstName}
              className={styles.field}
            >
              <input
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (profileErrors.errors.firstName)
                    profileErrors.clearError('firstName');
                }}
              />
            </Field>
            <Field
              label="Apelido"
              required
              error={profileErrors.errors.lastName}
              className={styles.field}
            >
              <input
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (profileErrors.errors.lastName)
                    profileErrors.clearError('lastName');
                }}
              />
            </Field>
          </div>
          <div className={styles.row}>
            <Field
              label="Telemóvel"
              error={profileErrors.errors.phone}
              className={styles.field}
            >
              <PhoneInput
                value={phone}
                onChange={(v) => {
                  setPhone(v);
                  if (profileErrors.errors.phone) profileErrors.clearError('phone');
                }}
                invalid={Boolean(profileErrors.errors.phone)}
              />
            </Field>
            <Field
              label="NIF"
              error={profileErrors.errors.nif}
              className={styles.field}
            >
              <input
                value={nif}
                onChange={(e) => {
                  setNif(e.target.value);
                  if (profileErrors.errors.nif) profileErrors.clearError('nif');
                }}
                maxLength={9}
                inputMode="numeric"
              />
            </Field>
          </div>
          {profileFeedback ? (
            <p
              className={profileFeedback.kind === 'success' ? styles.success : styles.error}
              role="status"
              aria-live="polite"
            >
              {profileFeedback.text}
            </p>
          ) : null}
          <div className={styles.actions}>
            <Button type="submit" variant="primary">{t.common.save}</Button>
          </div>
        </form>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t.account.changePassword}</h2>
        <form onSubmit={changePassword} className={styles.form} noValidate>
          <Field
            label={t.account.currentPassword}
            required
            error={passwordErrors.errors.currentPassword}
            className={styles.field}
          >
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                if (passwordErrors.errors.currentPassword)
                  passwordErrors.clearError('currentPassword');
              }}
              autoComplete="current-password"
            />
          </Field>
          <div className={styles.row}>
            <Field
              label={t.account.newPassword}
              required
              error={passwordErrors.errors.newPassword}
              className={styles.field}
            >
              <input
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (passwordErrors.errors.newPassword)
                    passwordErrors.clearError('newPassword');
                }}
                autoComplete="new-password"
              />
            </Field>
            <Field
              label="Confirmar palavra-passe"
              required
              error={passwordErrors.errors.confirmPassword}
              className={styles.field}
            >
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (passwordErrors.errors.confirmPassword)
                    passwordErrors.clearError('confirmPassword');
                }}
                autoComplete="new-password"
              />
            </Field>
          </div>
          <div className={styles.strength} aria-live="polite">
            <div className={styles.strengthBar} aria-hidden>
              {[1, 2, 3, 4].map((level) => {
                const filled = strength.score >= level;
                const segmentClasses = [
                  styles.strengthSegment,
                  filled ? styles.strengthSegmentFilled : '',
                  filled ? styles[`strengthLevel${strength.score}`] : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return <span key={level} className={segmentClasses} />;
              })}
            </div>
            <p className={styles.strengthLabel}>
              Força da palavra-passe: <strong>{strength.label}</strong>
            </p>
          </div>
          <p className={styles.hint}>
            Mínimo 8 caracteres. Combina letras maiúsculas, minúsculas, números e símbolos para
            uma palavra-passe mais segura.
          </p>
          {passwordFeedback ? (
            <p
              className={passwordFeedback.kind === 'success' ? styles.success : styles.error}
              role="status"
              aria-live="polite"
            >
              {passwordFeedback.text}
            </p>
          ) : null}
          <div className={styles.actions}>
            <Button type="submit" variant="primary">{t.common.save}</Button>
          </div>
        </form>
      </section>
    </>
  );
}
